import { Injectable } from '@angular/core';
import {
  TilePositionParameters,
  NavigationButtonsCoordinates,
  GALLERY_VIEW_NAVIGATION_BUTTONS_HEIGHT,
  GALLERY_VIEW_NAVIGATION_BUTTONS_WIDTH,
  GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN,
  SPACE_BETWEEN_CAROUSEL_TILES,
  GALLERY_VIEW_SPACING,
  GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
  MARGIN_LEFT_RIGHT_GALLERY_LIKE_VIEW,
  MARGIN_TOP_GALLERY_LIKE_VIEW,
  MARGIN_BOTTOM_GALLERY_LIKE_VIEW,
  EXPANDED_RIGHT_NAV_BUTTONS_HEIGHT,
  EXPANDED_RIGHT_NAV_BUTTONS_GAP,
  GALLERY_VIEW_MIN_ASPECT_RATIO,
  GALLERY_VIEW_MAX_ASPECT_RATIO,
  MAX_EXPANDED_ASPECT_RATIO,
  MIN_EXPANDED_ASPECT_RATIO,
  EXPANDED_TILE_FEED_DEFAULT_ASPECT_RATIO,
} from '../common/utils/tiles-packing-util';
import { FlagsService } from './flags.service';

enum ExpandedLayout {
  RIGHT = 'RIGHT',
  TOP = 'TOP',
}

interface ExpandedPrecomputed {
  canUse: boolean;
  carouselCapacity: number;
  carouselTileW: number;
  carouselTileH: number;
  expandedW: number;
  expandedH: number;
}

interface ExpandedReserved {
  remainingWidthAfterMargin: number;
  remainingHeightAfterMargin: number;
  anchorTopBase: number;
  anchorLeftBase: number;
  precomputedCarouselTileW: number;
  precomputedCarouselTileH: number;
  effectiveExpandedWidth: number;
  effectiveExpandedHeight: number;
  effectiveWidthForCarousel?: number;
  effectiveHeightForCarousel?: number;
}

interface ExpandedCapacityDecision {
  capacity: number;
  layout: ExpandedLayout;
  hasNav: boolean;
  reserved: ExpandedReserved;
}

interface GridConfig {
  cols: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
}

// Parameter grouping interfaces to reduce function parameter counts
interface ViewportDimensions {
  viewportWidth: number;
  viewportHeight: number;
}

interface TileConstraints {
  gutterX: number;
  gutterY: number;
  minTileWidth: number;
  minTileHeight: number;
}

interface AspectRatioConstraints {
  minAspectRatio: number;
  maxAspectRatio: number;
}

interface MarginConfig {
  marginTop: number;
  marginBottom: number;
  marginLeftRight: number;
}

export interface PinnedTileFeedDimensions {
  width: number;
  height: number;
  stretchToFill?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OptimizedGridLayoutService {
  // Grid configuration cache (by tiles count + rounded viewport + constraints)
  private gridConfigCache = new Map<string, GridConfig>();
  // Expanded view capacity/layout cache
  private expandedCapacityCache = new Map<string, ExpandedCapacityDecision>();
  private readonly MAX_CACHE_SIZE = 1000;
  private readonly CHANGE_THRESHOLD = 5; // pixels
  private readonly FLOAT_EPSILON = 1e-6; // tolerance for floating point precision errors in layout calculations

  constructor(private flagsService: FlagsService) {}

  /**
   * Get the minimum aspect ratio from feature flags or use default constant.
   */
  private getMinAspectRatio(): number {
    const flagValue =
      this.flagsService.featureFlagsVariables?.gallery_view_parameters?.min_aspect_ratio;
    return typeof flagValue === 'number' ? flagValue : GALLERY_VIEW_MIN_ASPECT_RATIO;
  }

  /**
   * Get the maximum aspect ratio from feature flags or use default constant.
   */
  private getMaxAspectRatio(): number {
    const flagValue =
      this.flagsService.featureFlagsVariables?.gallery_view_parameters?.max_aspect_ratio;
    return typeof flagValue === 'number' ? flagValue : GALLERY_VIEW_MAX_ASPECT_RATIO;
  }

  private setCacheEntry<T>(cache: Map<string, T>, key: string, value: T): void {
    if (cache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    cache.set(key, value);
  }

  private getContainerParameters(
    viewport: ViewportDimensions,
    margins: MarginConfig,
  ): {
    remainingWidthAfterMargin: number;
    remainingHeightAfterMargin: number;
    anchorTopBase: number;
    anchorLeftBase: number;
  } {
    const remainingWidthAfterMargin = viewport.viewportWidth - margins.marginLeftRight * 2;
    const remainingHeightAfterMargin =
      viewport.viewportHeight - margins.marginTop - margins.marginBottom;
    const anchorLeftBase = margins.marginLeftRight;

    const params = {
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
      anchorTopBase: margins.marginTop,
      anchorLeftBase,
    };
    return params;
  }

  // Quantization helpers for cache keys
  private roundForCache(value: number): number {
    return Math.round(value / this.CHANGE_THRESHOLD) * this.CHANGE_THRESHOLD;
  }

  private quantizeAspect(value: number): number {
    return Math.round(value * 100); // 2 decimals
  }

  private buildExpandedCapacityCacheKey(
    othersCount: number,
    viewport: ViewportDimensions,
    feedAspect: number,
    stretchToFill: boolean,
  ): string {
    return `exp-cap:${othersCount}-${this.roundForCache(
      viewport.viewportWidth,
    )}x${this.roundForCache(viewport.viewportHeight)}-far${this.quantizeAspect(feedAspect)}-fill${
      stretchToFill ? 1 : 0
    }`;
  }

  private buildGridConfigCacheKey(
    tileCount: number,
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
  ): string {
    return `grid:${tileCount}-${this.roundForCache(remainingWidthAfterMargin)}x${this.roundForCache(
      remainingHeightAfterMargin,
    )}`;
  }

  private findOptimalGridConfig(
    tileCount: number,
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
    constraints: TileConstraints,
    aspectRatios: AspectRatioConstraints,
  ): GridConfig {
    const { gutterX, gutterY, minTileWidth: minTileW, minTileHeight: minTileH } = constraints;
    const { minAspectRatio, maxAspectRatio } = aspectRatios;
    const cacheKey = this.buildGridConfigCacheKey(
      tileCount,
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
    );
    const cached = this.gridConfigCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Calculate max possible columns efficiently
    const maxPossibleCols = Math.floor(
      (remainingWidthAfterMargin + gutterX) / (minTileW + gutterX),
    );

    let bestConfigForThisCount: {
      cols: number;
      rows: number;
      tileW: number;
      tileH: number;
      tileArea: number;
    } | null = null;

    const maxCols = Math.min(tileCount, maxPossibleCols);

    // Iterate deterministically over all possible column counts
    for (let cols = 1; cols <= maxCols; cols++) {
      if (cols > maxCols) {
        continue;
      }

      const rows = Math.ceil(tileCount / cols);

      const totalGutterW = (cols - 1) * gutterX;
      const totalGutterH = (rows - 1) * gutterY;

      // Base cell size for a tile before constraints
      const baseTileW = (remainingWidthAfterMargin - totalGutterW) / cols;
      const baseTileH = (remainingHeightAfterMargin - totalGutterH) / rows;
      // Clamp aspect ratio instead of rejecting
      const unconstrainedAR = baseTileW / baseTileH;
      const targetAR = Math.max(minAspectRatio, Math.min(maxAspectRatio, unconstrainedAR));

      // Option 1: width limited by height
      const widthLimitedW = Math.min(baseTileW, baseTileH * targetAR);
      const widthLimitedH = widthLimitedW / targetAR;

      // Option 2: height limited by width
      const heightLimitedH = Math.min(baseTileH, baseTileW / targetAR);
      const heightLimitedW = heightLimitedH * targetAR;

      // Choose the option that yields the larger area (both fit within base cell)
      const option1Area = widthLimitedW * widthLimitedH;
      const option2Area = heightLimitedW * heightLimitedH;
      const adjW = option1Area >= option2Area ? widthLimitedW : heightLimitedW;
      const adjH = option1Area >= option2Area ? widthLimitedH : heightLimitedH;

      // Do not reject due to min size; rely on pagination/capacity and use available cell
      const area = adjW * adjH;
      if (!bestConfigForThisCount || area > bestConfigForThisCount.tileArea) {
        bestConfigForThisCount = { cols, rows, tileW: adjW, tileH: adjH, tileArea: area };
      }
    }

    const result = {
      cols: bestConfigForThisCount?.cols ?? 1,
      rows: bestConfigForThisCount?.rows ?? 1,
      tileWidth: bestConfigForThisCount?.tileW ?? minTileW,
      tileHeight: bestConfigForThisCount?.tileH ?? minTileH,
    };
    this.setCacheEntry(this.gridConfigCache, cacheKey, result);
    return result;
  }

  private calculateTotalWidthOrHeight(
    currentColsOrRowsNum: number,
    currentTileWidthOrHeight: number,
    spaceBetweenTiles: number,
  ): number {
    return (
      currentColsOrRowsNum * currentTileWidthOrHeight +
      (currentColsOrRowsNum - 1) * spaceBetweenTiles
    );
  }

  private setupNavigationButtonsPositionGallery(
    anchorTop: number,
    anchorLeft: number,
    totalTilesWidth: number,
    totalTilesHeight: number,
  ): NavigationButtonsCoordinates {
    // For gallery view, position navigation buttons on the sides
    const topLeftButton =
      anchorTop + totalTilesHeight / 2 - GALLERY_VIEW_NAVIGATION_BUTTONS_HEIGHT / 2;
    const leftLeftButton = anchorLeft + GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN;
    const topRightButton = topLeftButton;
    const leftRightButton =
      anchorLeft +
      totalTilesWidth -
      GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN -
      GALLERY_VIEW_NAVIGATION_BUTTONS_WIDTH;

    return {
      topLeftButton,
      leftLeftButton,
      topRightButton,
      leftRightButton,
    };
  }

  public getMaxGalleryTilesCapacity(
    viewport: ViewportDimensions,
    constraints?: TileConstraints,
    margins?: MarginConfig,
  ): number {
    const {
      gutterX = GALLERY_VIEW_SPACING,
      gutterY = GALLERY_VIEW_SPACING,
      minTileWidth = GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
      minTileHeight = GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
    } = constraints ?? {};
    const {
      marginTop = MARGIN_TOP_GALLERY_LIKE_VIEW,
      marginBottom = MARGIN_BOTTOM_GALLERY_LIKE_VIEW,
      marginLeftRight = MARGIN_LEFT_RIGHT_GALLERY_LIKE_VIEW,
    } = margins ?? {};
    const remainingWidthAfterMargin = viewport.viewportWidth - marginLeftRight * 2;
    const remainingHeightAfterMargin = viewport.viewportHeight - marginTop - marginBottom;
    return this.computeCapacity(remainingWidthAfterMargin, remainingHeightAfterMargin, {
      gutterX,
      gutterY,
      minTileWidth,
      minTileHeight,
    });
  }

  public findOptimalGalleryLayoutForTileCount(
    tileCount: number,
    viewport: ViewportDimensions,
    constraints?: TileConstraints,
    margins?: MarginConfig,
    aspectRatios?: AspectRatioConstraints,
  ): TilePositionParameters {
    const {
      gutterX = GALLERY_VIEW_SPACING,
      gutterY = GALLERY_VIEW_SPACING,
      minTileWidth = GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
      minTileHeight = GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
    } = constraints ?? {};
    const {
      marginTop = MARGIN_TOP_GALLERY_LIKE_VIEW,
      marginBottom = MARGIN_BOTTOM_GALLERY_LIKE_VIEW,
      marginLeftRight = MARGIN_LEFT_RIGHT_GALLERY_LIKE_VIEW,
    } = margins ?? {};
    // Use feature flag values if aspectRatios not provided
    const { minAspectRatio = this.getMinAspectRatio(), maxAspectRatio = this.getMaxAspectRatio() } =
      aspectRatios ?? {};

    // Reuse participant-aware container decision for nav reservation
    const containerParams = this.getContainerParameters(viewport, {
      marginTop,
      marginBottom,
      marginLeftRight,
    });

    const remainingWidthAfterMargin = containerParams.remainingWidthAfterMargin;
    const remainingHeightAfterMargin = containerParams.remainingHeightAfterMargin;

    const gridConfig = this.findOptimalGridConfig(
      tileCount,
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
      { gutterX, gutterY, minTileWidth, minTileHeight },
      { minAspectRatio, maxAspectRatio },
    );

    // Convert and center the grid
    return this.convertToGalleryTilePositionParameters(
      gridConfig,
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
      gutterX,
      gutterY,
      containerParams.anchorTopBase,
      containerParams.anchorLeftBase,
    );
  }

  private convertToGalleryTilePositionParameters(
    gridConfig: GridConfig,
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
    gutterX: number,
    gutterY: number,
    marginTop: number,
    marginLeftRight: number,
  ): TilePositionParameters {
    const currentColsNum = gridConfig.cols;
    const currentRowsNum = gridConfig.rows;
    const currentTileWidth = gridConfig.tileWidth;
    const currentTileHeight = gridConfig.tileHeight;

    const totalTilesWidth = this.calculateTotalWidthOrHeight(
      currentColsNum,
      currentTileWidth,
      gutterX,
    );
    const totalTilesHeight = this.calculateTotalWidthOrHeight(
      currentRowsNum,
      currentTileHeight,
      gutterY,
    );

    // Center the tiles
    const spaceToCenterWidth = Math.max((remainingWidthAfterMargin - totalTilesWidth) / 2, 0);
    const spaceToCenterHeight = Math.max((remainingHeightAfterMargin - totalTilesHeight) / 2, 0);

    const anchorTop = marginTop + spaceToCenterHeight;
    const anchorLeft = marginLeftRight + spaceToCenterWidth;

    return {
      currentTileWidth,
      currentTileHeight,
      currentColsNum,
      currentRowsNum,
      spaceBetweenTiles: gutterX, // Using gutterX for both dimensions for simplicity
      anchorTop,
      anchorLeft,
      anchorLeftLastRow: anchorLeft, // Default to regular anchorLeft; component will override if needed
      navigationButtonsCoordinates: this.setupNavigationButtonsPositionGallery(
        anchorTop,
        anchorLeft,
        totalTilesWidth,
        totalTilesHeight,
      ),
    };
  }

  public clearCache(): void {
    this.gridConfigCache.clear();
    this.expandedCapacityCache.clear();
  }

  private computeCapacity(
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
    constraints: TileConstraints,
  ): number {
    const maxCols = Math.max(
      1,
      Math.floor(
        (remainingWidthAfterMargin + constraints.gutterX) /
          (constraints.minTileWidth + constraints.gutterX),
      ),
    );
    const maxRows = Math.max(
      1,
      Math.floor(
        (remainingHeightAfterMargin + constraints.gutterY) /
          (constraints.minTileHeight + constraints.gutterY),
      ),
    );
    return maxCols * maxRows;
  }

  // ===== Expanded View Optimization =====

  /**
   * Compute how many carousel tiles can fit for Expanded view when carousel is on the right as a single column.
   */
  private computeExpandedRightCarouselCapacity(
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
    constraints: TileConstraints,
    desiredExpandedWidth: number,
    preferredAspectRatio: number,
  ): ExpandedPrecomputed {
    const { gutterX, gutterY, minTileWidth, minTileHeight } = constraints;
    // Try to allocate a single column for carousel. If leftover width is too small,
    // shrink expanded width to reserve at least minTileWidth for the carousel.
    let expandedW = desiredExpandedWidth;
    let leftoverWidth = remainingWidthAfterMargin - expandedW - gutterX;
    if (leftoverWidth < minTileWidth) {
      expandedW = Math.max(0, remainingWidthAfterMargin - gutterX - minTileWidth);
      leftoverWidth = remainingWidthAfterMargin - expandedW - gutterX;
      if (leftoverWidth < minTileWidth - this.FLOAT_EPSILON) {
        return {
          canUse: false,
          carouselCapacity: 0,
          carouselTileW: 0,
          carouselTileH: 0,
          expandedW,
          expandedH: 0,
        };
      }
    }
    // Compute smallest feasible height within AR and size constraints to maximize rows
    const minHByAR = leftoverWidth / Math.max(preferredAspectRatio, 0.1);
    const lowerHBound = Math.max(minTileHeight, minHByAR);
    const baseTileH = Math.min(lowerHBound, remainingHeightAfterMargin);

    // Calculate how many tiles can fit: n tiles need n*tileH + (n-1)*gutterY space
    // Use FLOAT_EPSILON to handle accumulated floating-point precision errors
    const rawRows = (remainingHeightAfterMargin + gutterY) / (baseTileH + gutterY);
    const rows = Math.max(1, Math.floor(rawRows + this.FLOAT_EPSILON));

    return {
      canUse: true,
      carouselCapacity: rows,
      carouselTileW: leftoverWidth,
      carouselTileH: baseTileH,
      expandedW: expandedW,
      // expandedH is not used from here in the RIGHT view, but we need to return it for consistency
      expandedH: remainingHeightAfterMargin,
    };
  }

  /**
   * Compute how many carousel tiles can fit for Expanded view when carousel is on the top as a single row.
   */
  private computeExpandedTopCarouselCapacity(
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
    constraints: TileConstraints,
    expandedAspectRatio: number,
    carouselMinAspect: number,
    stretchToFill = false,
  ): ExpandedPrecomputed {
    const { gutterX, gutterY, minTileWidth, minTileHeight } = constraints;
    // Force carousel to be 1 row with height = minTileHeight
    const carouselHeight = minTileHeight;
    const tileWByAspect = carouselHeight * Math.max(carouselMinAspect, 0.1);
    const effectiveHeight = Math.max(remainingHeightAfterMargin - carouselHeight - gutterY, 0);
    const maxExpandedWidthByHeight = effectiveHeight * expandedAspectRatio;
    const expandedMaxW = stretchToFill
      ? remainingWidthAfterMargin
      : Math.min(remainingWidthAfterMargin, maxExpandedWidthByHeight);
    const expandedMaxH = stretchToFill ? effectiveHeight : expandedMaxW / expandedAspectRatio;
    const perTileW = Math.max(minTileWidth, tileWByAspect);
    const cols = Math.max(
      1,
      Math.floor((remainingWidthAfterMargin + gutterX) / (perTileW + gutterX)),
    );
    return {
      canUse: true,
      carouselCapacity: cols,
      carouselTileW: perTileW,
      carouselTileH: carouselHeight,
      expandedW: expandedMaxW,
      expandedH: expandedMaxH,
    };
  }

  // Decision helpers that encapsulate nav-reservation logic and return full info
  private decideRightCarouselCapacity(
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
    constraints: TileConstraints,
    desiredExpandedWidth: number,
    preferredAspectRatio: number,
    othersCount: number,
  ): {
    canUse: boolean;
    capacity: number;
    hasNav: boolean;
    effectiveHeightForCarousel: number;
    expandedW: number;
    expandedH: number;
    carouselTileW: number;
    carouselTileH: number;
  } {
    // First, compute base capacity with the desired expanded width
    const base = this.computeExpandedRightCarouselCapacity(
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
      constraints,
      desiredExpandedWidth,
      preferredAspectRatio,
    );
    if (!base.canUse) {
      return {
        canUse: false,
        capacity: 0,
        hasNav: false,
        effectiveHeightForCarousel: remainingHeightAfterMargin,
        expandedW: base.expandedW,
        expandedH: remainingHeightAfterMargin,
        carouselTileW: 0,
        carouselTileH: 0,
      };
    }

    // Check if we need navigation
    const hasNav = othersCount > base.carouselCapacity;

    // Smart logic: If we have navigation and carousel isn't at minimum width,
    // expand the pinned tile to make carousel narrower and fit more tiles
    let optimizedExpandedWidth = base.expandedW;
    let canFitAllAfterOptimization = false;

    if (hasNav && base.carouselTileW > constraints.minTileWidth) {
      // Calculate the carousel tile height needed to fit all tiles
      const idealCarouselTileH =
        (remainingHeightAfterMargin - (othersCount - 1) * constraints.gutterY) / othersCount;

      // Check if we can fit all tiles (no nav needed after optimization)
      canFitAllAfterOptimization = idealCarouselTileH >= constraints.minTileHeight;

      // Clamp to minimum - even if we can't fit all, show as many as possible
      const targetCarouselTileH = Math.max(constraints.minTileHeight, idealCarouselTileH);

      // Calculate carousel width needed for this height (maintaining aspect ratio)
      const neededCarouselW = Math.max(
        constraints.minTileWidth,
        targetCarouselTileH * preferredAspectRatio,
      );

      // Expand the pinned tile to take remaining space
      optimizedExpandedWidth = remainingWidthAfterMargin - neededCarouselW - constraints.gutterX;
    }

    // Determine height based on whether we need nav buttons
    const needsNavButtons = hasNav && !canFitAllAfterOptimization;
    const computeHeight = needsNavButtons
      ? Math.max(
          0,
          remainingHeightAfterMargin -
            (EXPANDED_RIGHT_NAV_BUTTONS_HEIGHT + GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN),
        )
      : remainingHeightAfterMargin;

    // Recompute only if width changed or we need nav buttons
    const recomputed =
      optimizedExpandedWidth !== base.expandedW || needsNavButtons
        ? this.computeExpandedRightCarouselCapacity(
            remainingWidthAfterMargin,
            computeHeight,
            constraints,
            optimizedExpandedWidth,
            preferredAspectRatio,
          )
        : base;

    return {
      canUse: true,
      capacity: recomputed.carouselCapacity,
      hasNav: othersCount > recomputed.carouselCapacity,
      effectiveHeightForCarousel: computeHeight,
      expandedW: recomputed.expandedW,
      expandedH: remainingHeightAfterMargin,
      carouselTileW: recomputed.carouselTileW,
      carouselTileH: recomputed.carouselTileH,
    };
  }

  private decideTopCarouselCapacity(
    remainingWidthAfterMargin: number,
    remainingHeightAfterMargin: number,
    constraints: TileConstraints,
    expandedAspectRatio: number,
    carouselMinAspect: number,
    othersCount: number,
    stretchToFill: boolean,
  ): {
    canUse: boolean;
    capacity: number;
    hasNav: boolean;
    effectiveWidthForCarousel: number;
    expandedW: number;
    expandedH: number;
    carouselTileW: number;
    carouselTileH: number;
  } {
    const base = this.computeExpandedTopCarouselCapacity(
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
      constraints,
      expandedAspectRatio,
      carouselMinAspect,
      stretchToFill,
    );
    let hasNav = othersCount > base.carouselCapacity;
    let effectiveWidth = remainingWidthAfterMargin;
    let recomputed = base;
    if (hasNav) {
      const widthWithNav = Math.max(
        0,
        remainingWidthAfterMargin -
          2 * (GALLERY_VIEW_NAVIGATION_BUTTONS_WIDTH + GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN),
      );
      recomputed = this.computeExpandedTopCarouselCapacity(
        widthWithNav,
        remainingHeightAfterMargin,
        constraints,
        expandedAspectRatio,
        carouselMinAspect,
        stretchToFill,
      );
      effectiveWidth = widthWithNav;
      hasNav = othersCount > recomputed.carouselCapacity;
    }
    return {
      canUse: true,
      capacity: recomputed.carouselCapacity,
      hasNav,
      effectiveWidthForCarousel: effectiveWidth,
      expandedW: recomputed.expandedW,
      expandedH: recomputed.expandedH,
      carouselTileW: recomputed.carouselTileW,
      carouselTileH: recomputed.carouselTileH,
    };
  }

  public getExpandedViewPreferredCarouselCapacity(
    othersCount: number,
    viewport: ViewportDimensions,
    feed: PinnedTileFeedDimensions,
    carouselPreferredAspects = MAX_EXPANDED_ASPECT_RATIO,
    carouselMinAspect = MIN_EXPANDED_ASPECT_RATIO,
    constraints?: TileConstraints,
    margins?: MarginConfig,
  ): ExpandedCapacityDecision {
    const {
      gutterX = SPACE_BETWEEN_CAROUSEL_TILES,
      gutterY = SPACE_BETWEEN_CAROUSEL_TILES,
      minTileWidth = GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
      minTileHeight = GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
    } = constraints ?? {};
    const {
      marginTop = MARGIN_TOP_GALLERY_LIKE_VIEW,
      marginBottom = MARGIN_BOTTOM_GALLERY_LIKE_VIEW,
      marginLeftRight = MARGIN_LEFT_RIGHT_GALLERY_LIKE_VIEW,
    } = margins ?? {};

    // Cache lookup
    const feedAspect =
      feed.width > 0 && feed.height > 0
        ? feed.width / feed.height
        : EXPANDED_TILE_FEED_DEFAULT_ASPECT_RATIO;
    const stretchToFill = !!feed.stretchToFill;
    const cacheKey = this.buildExpandedCapacityCacheKey(
      othersCount,
      viewport,
      feedAspect,
      stretchToFill,
    );
    const cached = this.expandedCapacityCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    // For expanded view, we first compute container WITHOUT reserving nav space
    // since nav placement depends on whether carousel is RIGHT or TOP.
    const { remainingWidthAfterMargin, remainingHeightAfterMargin, anchorTopBase, anchorLeftBase } =
      this.getContainerParameters(viewport, { marginTop, marginBottom, marginLeftRight });
    // Height bound first for expanded tile, unless it may stretch and can claim the full width
    const desiredExpandedW = stretchToFill
      ? remainingWidthAfterMargin
      : Math.min(remainingWidthAfterMargin, remainingHeightAfterMargin * feedAspect);

    const rightDecision = this.decideRightCarouselCapacity(
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
      { gutterX, gutterY, minTileWidth, minTileHeight },
      desiredExpandedW,
      carouselPreferredAspects,
      othersCount,
    );

    const topDecision = this.decideTopCarouselCapacity(
      remainingWidthAfterMargin,
      remainingHeightAfterMargin,
      { gutterX, gutterY, minTileWidth, minTileHeight },
      feedAspect,
      carouselMinAspect,
      othersCount,
      stretchToFill,
    );
    // Decide by expanded tile area (favor RIGHT if areas are equal and RIGHT is usable)
    const rightUsable = rightDecision.canUse;
    const rightExpandedH = stretchToFill
      ? remainingHeightAfterMargin
      : Math.min(remainingHeightAfterMargin, rightDecision.expandedW / feedAspect);
    const rightArea = rightUsable ? rightDecision.expandedW * rightExpandedH : -1;
    const topArea = topDecision.expandedW * topDecision.expandedH;

    if (rightUsable && rightArea >= topArea - this.FLOAT_EPSILON) {
      const resultRight = {
        capacity: rightDecision.capacity,
        layout: ExpandedLayout.RIGHT,
        hasNav: rightDecision.hasNav,
        reserved: {
          remainingWidthAfterMargin,
          remainingHeightAfterMargin,
          anchorTopBase,
          anchorLeftBase,
          precomputedCarouselTileW: rightDecision.carouselTileW,
          precomputedCarouselTileH: rightDecision.carouselTileH,
          effectiveExpandedWidth: rightDecision.expandedW,
          effectiveExpandedHeight: rightExpandedH,
          effectiveHeightForCarousel: rightDecision.effectiveHeightForCarousel,
        },
      };
      this.setCacheEntry(this.expandedCapacityCache, cacheKey, resultRight);
      return resultRight;
    }

    const resultTop = {
      capacity: topDecision.capacity,
      layout: ExpandedLayout.TOP,
      hasNav: topDecision.hasNav,
      reserved: {
        remainingWidthAfterMargin,
        remainingHeightAfterMargin,
        anchorTopBase,
        anchorLeftBase,
        precomputedCarouselTileW: topDecision.carouselTileW,
        precomputedCarouselTileH: topDecision.carouselTileH,
        effectiveExpandedWidth: topDecision.expandedW,
        effectiveExpandedHeight: topDecision.expandedH,
        effectiveWidthForCarousel: topDecision.effectiveWidthForCarousel,
      },
    };
    this.setCacheEntry(this.expandedCapacityCache, cacheKey, resultTop);
    return resultTop;
  }

  public findOptimalExpandedLayoutForTileCount(
    tilesOnCurrentPage: number,
    othersCount: number,
    viewport: ViewportDimensions,
    feed: PinnedTileFeedDimensions,
    constraints?: TileConstraints,
    margins?: MarginConfig,
  ): TilePositionParameters {
    const {
      gutterX = SPACE_BETWEEN_CAROUSEL_TILES,
      gutterY = SPACE_BETWEEN_CAROUSEL_TILES,
      minTileHeight = GALLERY_LIKE_VIEW_MIN_TILE_WIDTH_HEIGHT,
    } = constraints ?? {};
    const aspect = feed.width > 0 && feed.height > 0 ? feed.width / feed.height : 16 / 9;

    const { capacity, layout, hasNav, reserved } = this.getExpandedViewPreferredCarouselCapacity(
      othersCount,
      viewport,
      feed,
      MAX_EXPANDED_ASPECT_RATIO,
      MIN_EXPANDED_ASPECT_RATIO,
      constraints,
      margins,
    );

    const availableWidth = reserved.remainingWidthAfterMargin;
    const availableHeight = reserved.remainingHeightAfterMargin;
    const anchorTopBase = reserved.anchorTopBase;
    const anchorLeftBase = reserved.anchorLeftBase;
    const precomputedCarouselTileW = reserved.precomputedCarouselTileW;
    const precomputedCarouselTileH = reserved.precomputedCarouselTileH;

    // Compute concrete sizes for chosen layout
    if (layout === ExpandedLayout.RIGHT) {
      const effectiveHeightForCarousel = reserved.effectiveHeightForCarousel ?? availableHeight;
      const desiredExpandedW = Math.min(
        reserved.effectiveExpandedWidth ?? Math.min(availableWidth, availableHeight * aspect),
        availableWidth,
      );
      const leftoverWidth = availableWidth - desiredExpandedW - gutterX;

      // Compute maximum rows then clamp to tilesOnCurrentPage
      const maxRows = Math.max(
        1,
        Math.floor((effectiveHeightForCarousel + gutterY) / (minTileHeight + gutterY)),
      );
      const rows = Math.min(maxRows, Math.max(1, tilesOnCurrentPage));
      const carouselTileW = precomputedCarouselTileW ?? leftoverWidth;
      const carouselTileH =
        precomputedCarouselTileH ?? (effectiveHeightForCarousel - (rows - 1) * gutterY) / rows;
      const totalCarouselH = rows > 0 ? (carouselTileH + gutterY) * rows - gutterY : 0;

      const anchorLeftCarousel = anchorLeftBase + desiredExpandedW + gutterX;

      // Center vertically within the effective carousel region (exclude reserved nav height if present)
      const verticalFreeSpaceCarousel = effectiveHeightForCarousel - totalCarouselH;
      const anchorTopCarousel = anchorTopBase + Math.max(verticalFreeSpaceCarousel / 2, 0);

      const spaceToCenterExpanded = feed.stretchToFill
        ? 0
        : Math.max((availableHeight - desiredExpandedW / aspect) / 2, 0);
      const expandedTileHeight = feed.stretchToFill
        ? availableHeight
        : Math.min(availableHeight, desiredExpandedW / aspect);
      const anchorTopExpanded = anchorTopBase + spaceToCenterExpanded;
      const anchorLeftExpanded = anchorLeftBase;

      return this.buildExpandedTilePositionParameters({
        layout: ExpandedLayout.RIGHT,
        carouselGrid: { cols: 1, rows },
        tileSizes: {
          carouselTileW,
          carouselTileH,
          expandedTileWidth: desiredExpandedW,
          expandedTileHeight,
        },
        anchors: {
          carouselTop: anchorTopCarousel,
          carouselLeft: anchorLeftCarousel,
          expandedTop: anchorTopExpanded,
          expandedLeft: anchorLeftExpanded,
        },
        container: {
          top: anchorTopBase,
          height: availableHeight,
          left: anchorLeftBase,
          width: availableWidth,
        },
        totals: { gutter: gutterY, carouselW: carouselTileW, carouselH: totalCarouselH },
      });
    } else {
      // TOP carousel
      const cols = Math.max(1, Math.min(capacity, Math.max(tilesOnCurrentPage, 1)));
      // Nav buttons float outside the carousel like Gallery View; do not reserve width
      const effectiveWidthForCarousel = reserved.effectiveWidthForCarousel ?? availableWidth;
      const carouselTileW =
        precomputedCarouselTileW > 0
          ? precomputedCarouselTileW
          : (effectiveWidthForCarousel - (cols - 1) * gutterX) / cols;
      const totalCarouselW = (carouselTileW + gutterX) * cols - gutterX;

      const spaceToCenterCarousel = Math.max((effectiveWidthForCarousel - totalCarouselW) / 2, 0);
      // Offset carousel inward when nav is present on both sides
      const leftOffset = hasNav
        ? GALLERY_VIEW_NAVIGATION_BUTTONS_WIDTH + GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN
        : 0;
      const anchorLeftCarousel = anchorLeftBase + leftOffset + spaceToCenterCarousel;

      const expandedTileWidth = reserved.effectiveExpandedWidth;
      const expandedTileHeight = reserved.effectiveExpandedHeight;

      // Center the whole block (carousel + gap + expanded) vertically
      const totalBlockH = precomputedCarouselTileH + gutterY + expandedTileHeight;
      const blockTop = anchorTopBase + Math.max((availableHeight - totalBlockH) / 2, 0);
      const anchorTopCarousel = blockTop;
      const anchorTopExpanded = blockTop + precomputedCarouselTileH + gutterY;
      const anchorLeftExpanded =
        anchorLeftBase + Math.max((availableWidth - expandedTileWidth) / 2, 0);

      return this.buildExpandedTilePositionParameters({
        layout: ExpandedLayout.TOP,
        carouselGrid: { cols, rows: 1 },
        tileSizes: {
          carouselTileW,
          carouselTileH: precomputedCarouselTileH,
          expandedTileWidth,
          expandedTileHeight,
        },
        anchors: {
          carouselTop: anchorTopCarousel,
          carouselLeft: anchorLeftCarousel,
          expandedTop: anchorTopExpanded,
          expandedLeft: anchorLeftExpanded,
        },
        container: {
          top: anchorTopBase,
          height: availableHeight,
          left: anchorLeftBase,
          width: availableWidth,
        },
        totals: { gutter: gutterX, carouselW: totalCarouselW, carouselH: precomputedCarouselTileH },
      });
    }
  }

  private buildExpandedTilePositionParameters(params: {
    layout: ExpandedLayout;
    carouselGrid: { cols: number; rows: number };
    tileSizes: {
      carouselTileW: number;
      carouselTileH: number;
      expandedTileWidth: number;
      expandedTileHeight: number;
    };
    anchors: {
      carouselTop: number;
      carouselLeft: number;
      expandedTop: number;
      expandedLeft: number;
    };
    container: { top: number; height: number; left: number; width: number };
    totals: { gutter: number; carouselW: number; carouselH: number };
  }): TilePositionParameters {
    const { layout, carouselGrid, tileSizes, anchors, container, totals } = params;
    const nav = this.setupNavigationButtonsPositionExpanded({
      layout,
      carouselAnchor: { top: anchors.carouselTop, left: anchors.carouselLeft },
      carouselTotal: { width: totals.carouselW, height: totals.carouselH },
      container,
    });

    return {
      currentTileWidth: tileSizes.carouselTileW,
      currentTileHeight: tileSizes.carouselTileH,
      currentColsNum: carouselGrid.cols,
      currentRowsNum: carouselGrid.rows,
      spaceBetweenTiles: totals.gutter,
      anchorTop: anchors.carouselTop,
      anchorLeft: anchors.carouselLeft,
      expandedTileWidth: tileSizes.expandedTileWidth,
      expandedTileHeight: tileSizes.expandedTileHeight,
      anchorTopExpanded: anchors.expandedTop,
      anchorLeftExpanded: anchors.expandedLeft,
      navigationButtonsCoordinates: nav,
    };
  }

  private setupNavigationButtonsPositionExpanded(params: {
    layout: ExpandedLayout;
    carouselAnchor: { top: number; left: number };
    carouselTotal: { width: number; height: number };
    container: { top: number; height: number; left: number; width: number };
  }): NavigationButtonsCoordinates {
    const { layout, carouselAnchor, carouselTotal, container } = params;
    if (layout === ExpandedLayout.RIGHT) {
      // Buttons sit under the carousel, share the carousel width, with gap in-between
      const topLeftButtonBase = carouselAnchor.top + carouselTotal.height + 8;
      const maxTop = container.top + container.height - EXPANDED_RIGHT_NAV_BUTTONS_HEIGHT;
      const topLeftButton = Math.min(topLeftButtonBase, maxTop);

      // Equal widths under the carousel
      const buttonsGap = EXPANDED_RIGHT_NAV_BUTTONS_GAP;
      const perButtonWidth = Math.max(0, (carouselTotal.width - buttonsGap) / 2);

      const leftLeftButton = carouselAnchor.left;
      const topRightButton = topLeftButton;
      const leftRightButton = carouselAnchor.left + perButtonWidth + buttonsGap;

      return {
        topLeftButton,
        leftLeftButton,
        topRightButton,
        leftRightButton,
        widthLeftButton: perButtonWidth,
        widthRightButton: perButtonWidth,
      };
    }
    // TOP carousel: buttons besides the carousel row
    const topLeftButton =
      carouselAnchor.top + carouselTotal.height / 2 - GALLERY_VIEW_NAVIGATION_BUTTONS_HEIGHT / 2;
    const leftLeftButton =
      carouselAnchor.left -
      (GALLERY_VIEW_NAVIGATION_BUTTONS_WIDTH + GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN);
    const topRightButton = topLeftButton;
    const leftRightButton =
      carouselAnchor.left + carouselTotal.width + GALLERY_VIEW_NAVIGATION_BUTTONS_MARGIN;
    return { topLeftButton, leftLeftButton, topRightButton, leftRightButton };
  }
}
